import { AllMetrics, MappedName } from '@pubstack/common/src/analytics/query'
import { NonNullable } from '@pubstack/common/src/assertion'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { compareNames } from '@pubstack/common/src/sort'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { CellContentWithSublabel, DataTableCell, DetailedDataTableCell, GaugeDataTableCell } from '~/components/DataTable/DataTableCell'
import { DataTableColumnData, DataTableSorting, DetailInfo } from '~/components/DataTable/DataTableTypes'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Tooltip } from '~/components/Tooltip'
import { Formula } from '~/modules/analytics/common/formulas/operation'
import { Status } from '~/types/utils'
import { isNotApplicable } from '~/utils/analytics'
import { GEO_COUNTRIES, GeoCountryCode } from '~/utils/GeoCountries'

type ColumnStatusConfig = {
  value: number | undefined
  warning: number
  alert: number
  defaultValue?: 'success' | undefined
}
type BaseConfig = {
  status?: Omit<ColumnStatusConfig, 'value'>
  tooltip?: string | ReactElement
}
type NameConfig<P extends string> = Pick<BaseConfig, 'status'> & {
  sortLabel: string
  mappedName?: MappedName
  showLabelAsTooltip?: boolean
} & (
    | {
        displayedAs: 'gauge'
        getRawValue: (_: { data: Data<P>; index: number }) => number
        max?: number
      }
    | {
        displayedAs: 'detail'
        getDetails: (_: { data: Data<P>; index: number }) => DetailInfo[]
      }
    | {
        displayedAs?: 'text'
      }
  )
export type AnalyticsDataTableOtherConfig<P extends string, T extends Partial<AllMetrics> = any> = BaseConfig & {
  propertyName: P
  formula: Formula<T>
  isComputable?: (_: { rawData: RawData; index: number; mappedName: MappedName }) => boolean
  cellTooltip?: (_: { rawData: RawData; index: number; currencySymbol: CurrencySymbol }) => string | undefined
  cellStatus?: (rawValue: number | undefined) => Status | undefined
}

export type AnalyticsDataTableConfigs<P extends string> = [NameConfig<P>, ...AnalyticsDataTableOtherConfig<P>[]][]

type RawData = Partial<AllMetrics> & {
  name: MappedName[]
}

type Data<K extends string> = {
  name: MappedName
} & {
  [key in K]: {
    rawValue: number | undefined
    percentage: number | undefined
    value: string
    tooltip?: string
  }
}
export type AnalyticsDataTableFilterData<K extends string> = Parameters<Data<K>[][]['filter']>[0]

export const getColumnStatus = ({ value, warning, alert, defaultValue }: ColumnStatusConfig): Status | undefined => {
  let mode
  if (warning > alert) {
    mode = 'below'
  } else {
    mode = 'above'
  }

  if (value === undefined) {
    return undefined
  }

  if (mode === 'above') {
    if (value >= alert) {
      return 'alert'
    }
    if (value >= warning) {
      return 'warning'
    }
  } else {
    if (value <= alert) {
      return 'alert'
    }
    if (value <= warning) {
      return 'warning'
    }
  }
  return defaultValue
}

const isMetrics = <T,>(value: T | T[] | undefined): value is T => value !== undefined && !Array.isArray(value)
const asCountryName = (name: GeoCountryCode | string): string => GEO_COUNTRIES[name as GeoCountryCode] || name

const getData = <P extends string>({
  rawData,
  sortValue,
  currencySymbol,
  dataConfigs,
  filterData,
  maxResults,
}: {
  rawData: RawData | undefined
  sortValue: P
  currencySymbol: CurrencySymbol
  dataConfigs: AnalyticsDataTableConfigs<P>
  filterData?: AnalyticsDataTableFilterData<P>
  maxResults?: number
}): Data<P>[][] => {
  if (!isMetrics(rawData)) {
    return []
  }

  return rawData.name
    .map((mappedName, index) =>
      dataConfigs.map((configs) => {
        const [nameConfig, ...otherConfigs] = configs
        return otherConfigs.reduce(
          (acc, config) => {
            const computable =
              config.formula.isComputable(rawData) &&
              (config.isComputable?.({
                rawData,
                index,
                mappedName,
              }) ??
                true)
            const d: Data<P> = {
              ...acc,
              [config.propertyName]: {
                rawValue: computable ? config.formula.compute(rawData, index) : undefined,
                percentage: computable ? config.formula.percentage(config.formula.compute(rawData, index), rawData) : undefined,
                value: computable ? config.formula.displayable(config.formula.compute(rawData, index), currencySymbol) : 'NA',
                tooltip: computable ? config.cellTooltip?.({ rawData, index, currencySymbol }) : undefined,
              },
            }

            return d
          },
          {
            name: nameConfig.mappedName ?? {
              value: rawData['name'][index].value,
              label: asCountryName(rawData['name'][index].label),
              sublabel: rawData['name'][index]?.sublabel,
            },
          } as Data<P>
        )
      })
    )
    .filter(filterData ?? (() => true))
    .sort(([a], [b]) => {
      if (sortValue === 'name') {
        return compareNames(a['name']['label'], b['name']['label'])
      } else {
        return (b[sortValue]?.rawValue ?? 0) - (a[sortValue]?.rawValue ?? 0)
      }
    })
    .slice(0, maxResults ?? Number.MAX_SAFE_INTEGER)
}

const getColumns = <P extends string>(configs: AnalyticsDataTableConfigs<P>, columnsConfigs: P[]): DataTableColumnData<Data<P>>[] => {
  const [, ...otherConfigs] = configs[0]
  return [
    {
      name: 'name',
      displaySort: true,
      render: ({ index, secondary, key, rows }) => {
        const { label, sublabel } = rows[index].name
        const nameConfig = configs[index][0]
        const Label = () => (nameConfig.showLabelAsTooltip ? <Tooltip title={label}>{label}</Tooltip> : label)
        const CellContent = () => (sublabel ? <CellContentWithSublabel label={<Label />} sublabel={sublabel || ''} /> : <Label />)

        if (nameConfig.displayedAs === 'text') {
          return (
            <DataTableCell key={key} secondary={secondary}>
              <CellContent />
            </DataTableCell>
          )
        } else if (nameConfig.displayedAs === 'gauge') {
          const value = nameConfig.getRawValue?.({ data: rows[index], index }) ?? 0
          const status = nameConfig.status
            ? getColumnStatus({
                ...nameConfig.status,
                value,
              })
            : undefined
          return (
            <GaugeDataTableCell key={key} secondary={secondary} status={status} gaugeValues={{ value, max: nameConfig.max }}>
              <CellContent />
            </GaugeDataTableCell>
          )
        } else if (nameConfig.displayedAs === 'detail') {
          return (
            <DetailedDataTableCell key={key} secondary={secondary} details={nameConfig.getDetails({ data: rows[index], index })}>
              <CellContent />
            </DetailedDataTableCell>
          )
        }
        return null as never
      },
    },
    ...columnsConfigs
      .map((cc) => otherConfigs.find((c) => c.propertyName === cc))
      .filter(NonNullable)
      .map((config): DataTableColumnData<Data<P>> => ({
        name: config.formula.name,
        render: ({ index, key, secondary, rows }) => {
          const { rawValue, value, tooltip } = rows[index][config.propertyName]
          const status = config.cellStatus
            ? config.cellStatus(rawValue)
            : config.status
              ? getColumnStatus({
                  ...config.status,
                  value: rawValue,
                })
              : undefined
          return (
            <DataTableCell key={key} secondary={secondary} status={status} isNotApplicable={isNotApplicable(value)}>
              {tooltip ? <Tooltip title={tooltip}>{value}</Tooltip> : value}
            </DataTableCell>
          )
        },
        tooltip: config.tooltip,
      })),
  ]
}

const useSorting = <P extends string>(configs: AnalyticsDataTableConfigs<P>, columnsConfigs: P[], defaultSort?: string): DataTableSorting<P | 'name'> => {
  const [, ...otherConfigs] = configs[0]
  const sortOptions: SelectOptionProp<P | 'name'>[] = [
    {
      label: 'Name',
      value: 'name' as const,
      trackingLabel: 'ana-breakdown-sorting-name-apply',
    },
    ...columnsConfigs
      .map((cc) => otherConfigs.find((c) => c.propertyName === cc))
      .filter(NonNullable)
      .map((config): SelectOptionProp<P> => ({
        label: config.formula.name,
        value: config.propertyName,
        trackingLabel: `ana-breakdown-sorting-${normalizeAsTrackingLabel(config.formula.name)}-apply`,
      })),
  ]

  const getDefaultOption = () => sortOptions.find(({ value }) => value === defaultSort) ?? sortOptions[1]

  const [sortValue, setSortValue] = useState<SelectOptionProp<P | 'name'>>(getDefaultOption())

  useEffect(() => {
    if (!sortOptions.find((o) => o.value === sortValue.value)) {
      // Resets the sort when the available options change and the current one is gone.
      // oxlint-disable-next-line react/set-state-in-effect
      setSortValue(getDefaultOption())
    }
  }, [sortOptions])

  return {
    sortValue,
    sortOptions,
    setSortValue,
  }
}

type AnalyticsDataTableProps<P extends string> = {
  rawData?: RawData
  currencySymbol: CurrencySymbol
  dataConfigs: AnalyticsDataTableConfigs<P>
  columnsConfigs: P[]
  filterData?: AnalyticsDataTableFilterData<P>
  maxResults?: number
  defaultSort?: P
}
export const useAnalyticsDataTable = <P extends string>({ dataConfigs, columnsConfigs, maxResults, filterData, rawData, currencySymbol, defaultSort }: AnalyticsDataTableProps<P>) => {
  const sorting = useSorting(dataConfigs, columnsConfigs, defaultSort)

  const data = useMemo(
    () => getData({ rawData, currencySymbol, sortValue: sorting.sortValue.value, dataConfigs, filterData, maxResults }),
    [rawData, currencySymbol, sorting.sortValue.value, dataConfigs, filterData, maxResults]
  )
  const columns = useMemo(() => getColumns(dataConfigs, columnsConfigs), [dataConfigs, columnsConfigs])

  return {
    data,
    columns,
    sorting,
  }
}
